/**
 * PWA Offline — SyncManager
 *
 * Singleton that manages FIFO queue of pending offline writes.
 * Processes pending_writes from IndexedDB on online event, manual trigger, or periodic interval.
 *
 * Design decisions (from design.md Section 5 — Sync Queue Flow):
 * - FIFO queue from pending_writes ordered by created_at
 * - Exponential backoff: 1s, 2s, 4s (up to 3 retries)
 * - On 409 Conflict: mark as 'failed', surface to user via ConflictModal
 * - On 5xx/network error: retry up to 3× with backoff
 * - Items >7 days old: mark as 'failed' with reason='expired'
 * - On success: delete from pending_writes, update local cache
 * - SyncManager.startFifo() processes all pending items
 */

import { apiClient } from '../services/apiClient';
import { db } from './db';
import type { PendingWrite, PendingWriteStatus } from './db';

// ── Constants ────────────────────────────────────────────────────────────────

const MAX_RETRIES = 3;
const RETRY_DELAYS_MS = [1000, 2000, 4000]; // exponential backoff: 1s, 2s, 4s
const EXPIRY_DAYS = 7;
const EXPIRY_MS = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

// ── Types ───────────────────────────────────────────────────────────────────

export interface SyncConflict {
    write: PendingWrite;
    serverData: Record<string, unknown>;
}

export interface SyncResult {
    processed: number;
    succeeded: number;
    failed: number;
    conflicts: SyncConflict[];
}

// Singleton instance state
let isProcessing = false;

// ── Helper: Sleep for given milliseconds ─────────────────────────────────────

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

// ── Helper: Check if a pending write is expired (>7 days old) ────────────────

function isExpired(write: PendingWrite): boolean {
    return Date.now() - write.created_at > EXPIRY_MS;
}

// ── Helper: Get entity API endpoint ────────────────────────────────────────

function getEntityEndpoint(entityType: string, entityId: number): string {
    switch (entityType) {
        case 'consultation':
            return `/consultations/${entityId}`;
        case 'appointment':
            return `/appointments/${entityId}`;
        default:
            return `/${entityType}/${entityId}`;
    }
}

// ── Helper: Mark a pending write as failed ──────────────────────────────────

async function markAsFailed(
    id: number,
    errorReason: string,
    retryCount?: number
): Promise<void> {
    const updates: Partial<PendingWrite> = {
        status: 'failed',
        error_reason: errorReason,
    };
    if (retryCount !== undefined) {
        updates.retry_count = retryCount;
    }
    await db.pending_writes.update(id, updates);
}

// ── Helper: Mark a pending write as syncing ─────────────────────────────────

async function markAsSyncing(id: number): Promise<void> {
    await db.pending_writes.update(id, { status: 'syncing' });
}

// ── Helper: Delete a pending write (success) ───────────────────────────────

async function deletePendingWrite(id: number): Promise<void> {
    await db.pending_writes.delete(id);
}

// ── Helper: Retry delay with exponential backoff ─────────────────────────────

async function delayWithBackoff(retryCount: number): Promise<void> {
    if (retryCount < RETRY_DELAYS_MS.length) {
        await sleep(RETRY_DELAYS_MS[retryCount]);
    }
}

// ── SyncManager Singleton ────────────────────────────────────────────────────

export const SyncManager = {
    /**
     * Whether SyncManager is currently processing the queue.
     * Useful for UI to show "syncing in progress" state.
     */
    getIsProcessing(): boolean {
        return isProcessing;
    },

    /**
     * Retry a specific pending write with X-Force-Update header.
     * Called when user chooses "Keep my version" in ConflictModal.
     *
     * @param writeId  — the pending_writes record id
     * @param payload  — the original payload (re-sent with force header)
     * @returns true on success, false on failure
     */
    async retryWithForce(writeId: number, payload: Record<string, unknown>): Promise<boolean> {
        const pending = await db.pending_writes.get(writeId);
        if (!pending) {
            console.warn(`[SyncManager] retryWithForce: write ${writeId} not found`);
            return false;
        }

        const endpoint = getEntityEndpoint(pending.entity_type, pending.entity_id);

        try {
            const response = await apiClient.patch(endpoint, payload, {
                headers: { 'X-Force-Update': 'true' },
            });

            if (response.status === 200 || response.status === 201 || response.status === 204) {
                await db.pending_writes.delete(writeId);
                console.log(`[SyncManager] retryWithForce: write ${writeId} synced successfully`);
                return true;
            }

            // Non-409 failure — mark failed
            await markAsFailed(writeId, `force retry failed: HTTP ${response.status}`);
            return false;
        } catch (error) {
            const errorObj = error as { response?: { status?: number } };
            const status = errorObj?.response?.status;
            // 409 shouldn't happen on force update, but handle it
            if (status === 409) {
                await markAsFailed(writeId, 'conflict even with force', 0);
            } else {
                await markAsFailed(writeId, `force retry error: ${status ?? 'network'}`);
            }
            return false;
        }
    },

    /**
     * Process all pending writes from IndexedDB FIFO queue.
     *
     * Flow:
     * 1. Fetch all pending writes ordered by created_at (oldest first)
     * 2. For each pending write:
     *    a. If expired (>7 days): mark as failed, skip
     *    b. If already syncing/failed/resolved: skip
     *    c. Mark as syncing
     *    d. Call API PATCH with the write payload
     *    e. On 200: delete from pending_writes
     *    f. On 409 Conflict: mark as failed, collect conflict for modal
     *    g. On 5xx/network error: retry up to 3× with exponential backoff
     *       - If all retries fail: mark as failed
     *
     * @returns SyncResult with counts and any conflicts that need user resolution
     */
    async startFifo(): Promise<SyncResult> {
        if (isProcessing) {
            console.log('[SyncManager] Already processing, skipping');
            return { processed: 0, succeeded: 0, failed: 0, conflicts: [] };
        }

        isProcessing = true;
        console.log('[SyncManager] Starting FIFO processing');

        const result: SyncResult = {
            processed: 0,
            succeeded: 0,
            failed: 0,
            conflicts: [],
        };

        try {
            // Fetch all pending writes ordered by created_at (FIFO)
            // Note: 'status' is not indexed, so we orderBy created_at and filter in memory
            const allPending = await db.pending_writes.orderBy('created_at').toArray();
            const pendingWrites = allPending.filter((w) => w.status === 'pending');

            console.log(`[SyncManager] Found ${pendingWrites.length} pending writes`);

            for (const write of pendingWrites) {
                if (write.id === undefined) continue;

                result.processed++;

                // Check if expired
                if (isExpired(write)) {
                    console.log(`[SyncManager] Write ${write.id} expired, marking as failed`);
                    await markAsFailed(write.id, 'expired');
                    result.failed++;
                    continue;
                }

                // Mark as syncing
                await markAsSyncing(write.id);

                // Process this write
                const processResult = await processWrite(write);

                if (processResult.success) {
                    result.succeeded++;
                } else if (processResult.conflict) {
                    result.conflicts.push(processResult.conflict);
                    result.failed++;
                } else {
                    result.failed++;
                }
            }

            console.log(
                `[SyncManager] FIFO complete: ${result.processed} processed, ` +
                    `${result.succeeded} succeeded, ${result.failed} failed, ` +
                    `${result.conflicts.length} conflicts`
            );

            return result;
        } finally {
            isProcessing = false;
        }
    },
};

/**
 * Process a single pending write with retry logic.
 *
 * @returns { success: true } on 200
 * @returns { conflict: SyncConflict } on 409
 * @returns { success: false } on retry exhaustion or other errors
 */
async function processWrite(
    write: PendingWrite
): Promise<{ success?: boolean; conflict?: SyncConflict }> {
    const endpoint = getEntityEndpoint(write.entity_type, write.entity_id);
    const payload = write.payload;

    let lastError: unknown = null;

    for (let retry = 0; retry <= MAX_RETRIES; retry++) {
        try {
            const response = await apiClient.patch(endpoint, payload);

            // Success
            if (response.status === 200) {
                if (write.id !== undefined) {
                    await deletePendingWrite(write.id);
                }
                console.log(
                    `[SyncManager] Successfully synced write ${write.id} ` +
                        `(${write.entity_type}:${write.entity_id})`
                );
                return { success: true };
            }

            // Conflict — server has newer data
            if (response.status === 409) {
                console.log(
                    `[SyncManager] Conflict on write ${write.id}: server has newer data`
                );
                if (write.id !== undefined) {
                    await markAsFailed(write.id, 'conflict', retry);
                }
                return {
                    conflict: {
                        write,
                        serverData: (response.data ?? {}) as Record<string, unknown>,
                    },
                };
            }

            // Other success-like status (201, 204, etc.)
            if (response.status === 201 || response.status === 204) {
                if (write.id !== undefined) {
                    await deletePendingWrite(write.id);
                }
                return { success: true };
            }
        } catch (error) {
            lastError = error;

            // Check if it's a network error (no response) or server error (5xx)
            const errorObj = error as { response?: { status?: number } };
            const status = errorObj?.response?.status;
            const isServerError = status !== undefined && status >= 500;
            const isNetworkError = status === undefined;

            if (isServerError || isNetworkError) {
                console.log(
                    `[SyncManager] Write ${write.id} failed (attempt ${retry + 1}/${MAX_RETRIES + 1}), ` +
                        `error: ${isNetworkError ? 'network error' : `HTTP ${status}`}`
                );

                if (retry < MAX_RETRIES) {
                    // Update retry count in DB
                    if (write.id !== undefined) {
                        await db.pending_writes.update(write.id, { retry_count: retry + 1 });
                    }
                    // Wait with exponential backoff before retry
                    await delayWithBackoff(retry);
                    continue;
                } else {
                    // Exhausted all retries
                    console.log(`[SyncManager] Write ${write.id} exhausted retries`);
                    if (write.id !== undefined) {
                        await markAsFailed(
                            write.id,
                            `sync failed after ${MAX_RETRIES + 1} attempts`,
                            retry
                        );
                    }
                    return { success: false };
                }
            }

            // Client error (4xx except 409) — don't retry
            console.log(
                `[SyncManager] Write ${write.id} failed with client error ${status}, not retrying`
            );
            if (write.id !== undefined) {
                await markAsFailed(
                    write.id,
                    `client error: ${status ?? 'unknown'}`,
                    retry
                );
            }
            return { success: false };
        }
    }

    // Should not reach here, but safety fallback
    if (write.id !== undefined) {
        await markAsFailed(write.id, 'unknown error');
    }
    return { success: false };
}

export default SyncManager;
